import { Component, OnInit, Inject, Output, EventEmitter, Input, AfterViewInit, ViewChild, ElementRef, ViewEncapsulation, OnDestroy } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MapPickerService } from './map-picker.service';

@Component({
  selector: 'app-map-picker',
  templateUrl: './map-picker.component.html',
  styleUrls: ['./map-picker.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class MapPickerComponent implements OnInit, OnDestroy, AfterViewInit {

  @Output() addressChange: EventEmitter<any> = new EventEmitter();
  @Input() standalone: boolean = false;
  @Input() module?: string;
  @Input() geoCoords: Array<any> = [];
  type: string = 'ADDRESS';
  mapOptions: any = {};
  map: google.maps.Map;
  marker: google.maps.Marker;
  geoCordinates: string;
  geoCodeTimeout: any;
  value: any;
  infoWindow: google.maps.InfoWindow;
  mapTimeout: any;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    private dialogRef: MatDialogRef<MapPickerComponent>,
    private eleRef: ElementRef,
    private _mapPickerService: MapPickerService
  ) { }


  closeDialog(): void {
    this.dialogRef.close();
  }

  typeChanged(): void {
    this.geoCordinates = '';
  }

  onAutocompleteSelected(event: any): void {
    this.value = event?.address;
    this.addressChange.emit(this.value);
    if(event?.address?.geoLocation?.latitude && event?.address?.geoLocation?.longitude){
      const coords = {
        lat: event?.address?.geoLocation?.latitude,
        lng: event?.address?.geoLocation?.longitude
      };
      this.map.setCenter(coords);
      this.map.setZoom(17);
      this.setMarker(coords);
    }
  }

  geocodeChange(event: InputEvent): void {
    this.searchGeoCode();
  }

  getCoords(): any {
    const coords = this.geoCordinates ? this.geoCordinates.split(',') : null;
    if(coords) {
      const latlng = {
        lat: parseFloat(coords[0]),
        lng: parseFloat(coords[1]),
      };
      return latlng;
    }
  }

  useAddress(): void {
    const data = {
      formGroupName: this.data?.data?.formGroupName,
      value: this.value
    };
    this._mapPickerService.getAddress.next(data);
    this.dialogRef.close();
  }

  searchGeoCode(): void {
    const latlng = this.getCoords();
    const geocoder = new google.maps.Geocoder();
    if(this.geoCodeTimeout) {
      clearTimeout(this.geoCodeTimeout);
    }
    this.geoCodeTimeout = setTimeout(() => {
      if(latlng && latlng.lat && latlng.lng) {
        geocoder.geocode({ location: latlng }, (response: any, status: string) => {
          if (status === "OK" && response && response?.length > 0) {
            this.map.setCenter(latlng);
            this.map.setZoom(17);
            this.setMarker(latlng);
            this.setInfoWindow(response);
            this.value = this.getAddressData(response, latlng);
            this.addressChange.emit(this.value);
          }
       });
      }
    }, 1000);
  }

  getAddressData(response: any[], latlng: any): any {
    const data = {};
    data['street'] = response[0]?.formatted_address ?? '';
    data['geoLocation'] = {
      latitude: latlng['lat'],
      longitude: latlng['lng']
    };
    response[0]?.address_components?.forEach((address)=>{
      switch(address['types'][0]){
          case 'locality' :
            data['city'] = address['long_name'] + ' ';
            break;
          case 'administrative_area_level_1' :
            data['state'] = address['long_name'] + ' ';
            break;
          case 'country' :
            data['country'] = {
             value: address['long_name'],
             label: address['short_name']
            };
            break;
          case 'postal_code' :
            data['zip_code'] = address['long_name'];
            break;
      }
    });
    return data;
  }

  setMarker(latlng: any): void{
    this.marker.setPosition(latlng);
  }

  setInfoWindow(response: any): void {
    if (response && Array.isArray(response) && response.length > 0 && response[0]?.formatted_address) {
      this.infoWindow.setContent(response[0].formatted_address);
      this.infoWindow.open(this.map, this.marker);
    }
  }
  
  initMap(coords: any): void {
    if (typeof google !== 'undefined' && google.maps && google.maps.Map) {
      this.loadGoogleMaps(coords);
    } else {
      if (this.mapTimeout) {
        clearTimeout(this.mapTimeout);
      }
      this.mapTimeout = setTimeout(() => {
        if (typeof google !== 'undefined' && google.maps && google.maps.Map) {
          this.loadGoogleMaps(coords);
        }
      }, 100);
    }
  }

  loadGoogleMaps(coords: any): void {
    const bodyEle: HTMLElement = this.eleRef?.nativeElement?.querySelector('#map-body');
    this.map = new google.maps.Map(
      bodyEle,
      {
        zoom: 17,
        center: coords,
        streetViewControl: false,
        disableDefaultUI: true
      }
    );
    this.marker = new google.maps.Marker({
      position: coords,
      map: this.map,
      draggable: true
    });

    this.infoWindow = new google.maps.InfoWindow();
      this.map.addListener('click', (event: any) => {
        this.setMarker(event.latLng);
        this.setMarkerLocation(event);
      });

      this.marker.addListener('dragend', (event: any) => {
        this.setMarker(event.latLng);
        this.marker.getPosition();
        this.setMarkerLocation(event);
      });
  }

  setMarkerLocation(event: any): void{
    const latlng = {
      lat: event.latLng.lat(),
      lng: event.latLng.lng(),
    };
    const geocoder = new google.maps.Geocoder();
    if(this.geoCodeTimeout) {
      clearTimeout(this.geoCodeTimeout);
    }
    this.geoCodeTimeout = setTimeout(() => {
      if(latlng && latlng.lat && latlng.lng) {
        geocoder.geocode({ location: latlng }, (response: any, status: string) => {
          if (status === "OK" && response && response?.length > 0) {
            this.map.setCenter(latlng);
            this.map.setZoom(17);
            this.setMarker(latlng);
            this.setInfoWindow(response);
            this.value = this.getAddressData(response, latlng);
            this.addressChange.emit(this.value);
          }
       });
      }
    }, 200);
  }

  ngAfterViewInit(): void {
    if(this.geoCoords.length > 0){
      this.initMap({lat : this.geoCoords[0], lng: this.geoCoords[1]});
    }
    else if (navigator.geolocation) {
      this.mapOptions.coords = {};
      navigator.geolocation.getCurrentPosition((position) => {
          this.mapOptions.coords.latitude = position.coords.latitude;
          this.mapOptions.coords.longitude = position.coords.longitude;
          this.initMap({lat : this.mapOptions.coords.latitude, lng: this.mapOptions.coords.longitude});
      }, (err)=>{
        this.initMap({lat : 47.580994, lng: -122.3180380});
      });
    }else{
      this.initMap({lat : 47.580994, lng: -122.3180380});
    }
  }

  ngOnInit(): void {
    this.value = '';
  }

  ngOnDestroy(): void {
    google.maps.event.clearInstanceListeners(this.map);
    google.maps.event.clearInstanceListeners(this.marker);
  }

}
